import * as React from 'react';
import {
  Badge,
  Button,
  Chip,
  ChipGroup,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Modal,
  ModalVariant,
  Tab,
  TabTitleText,
  Tabs,
  TextInput,
} from '@patternfly/react-core';
import { FilterIcon } from '@patternfly/react-icons';
import { unitValue } from '../../../../shared/riskregister/grouping';
import { defaults } from '../../../../shared/riskregister/viewState';
import { PICK_SECTION } from '../tabs';
import type { GroupDimension, Payload, ViewState } from '../../../../shared/riskregister/types';

const GROUP_OPTIONS: Array<{ value: GroupDimension; label: string }> = [
  { value: 'project', label: 'Project' },
  { value: 'cat', label: 'Risk category' },
  { value: 'epic', label: 'Initiative' },
  { value: 'assignee', label: 'Owner' },
  { value: 'svp', label: 'Platform area' },
];

const ACCOUNTABILITY_OPTIONS: Array<{ value: GroupDimension; label: string }> = [
  { value: 'manager', label: 'Team / manager' },
  { value: 'assignee', label: 'Owner' },
  { value: 'svp', label: 'Platform area' },
  { value: 'epic', label: 'Initiative' },
];

const SORT_OPTIONS: Array<{ value: ViewState['sort']; label: string }> = [
  { value: 'worst', label: 'Worst first' },
  { value: 'score', label: 'Risk score' },
  { value: 'overdue', label: 'Overdue days' },
  { value: 'idle', label: 'Idle days' },
  { value: 'age', label: 'Age' },
  { value: 'due', label: 'Due date' },
  { value: 'slips', label: 'Due-date slips' },
  { value: 'entered', label: 'Recently added' },
  { value: 'key', label: 'Ticket key' },
];

interface ActiveFilter {
  label: string;
  change: Partial<ViewState>;
}

export function activeFilters(state: ViewState, payload: Payload): ActiveFilter[] {
  const base = defaults(payload);
  const filters: ActiveFilter[] = [];
  const add = (key: keyof ViewState, label: string) => {
    if (state[key] !== base[key]) filters.push({ label, change: { [key]: base[key] } as Partial<ViewState> });
  };

  add('scope', `Scope: ${state.scope}`);
  add('stage', `Stage: ${state.stage}`);
  add('due', `Due: ${state.due}`);
  add('idle', `Idle: ${state.idle}`);
  add('score', `Score: ${state.score}`);
  add('owner', `Owner: ${state.owner}`);
  if (state.cat !== 'any') filters.push({ label: `Category: ${state.cat === '__nocat__' ? payload.no_cat_label : payload.categories[state.cat]}`, change: { cat: 'any' } });
  add('catsrc', `Category state: ${state.catsrc}`);
  add('pri', `Priority: ${state.pri}`);
  add('lane', `Queue lane: ${state.lane}`);
  add('duemove', `Due history: ${state.duemove}`);
  if (state.q) filters.push({ label: `Search: ${state.q}`, change: { q: '' } });
  if (state.unit) filters.push({ label: `Accountability: ${state.unit.slice(state.unit.indexOf(':') + 1)}`, change: { unit: '' } });

  return filters;
}

interface ViewControlsProps {
  payload: Payload;
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
  onReset: () => void;
  onRefresh: () => Promise<void>;
  onExport: () => void;
  onExportExcel: () => void;
  isRefreshing: boolean;
  asOfControl?: React.ReactNode;
}

const SelectControl: React.FunctionComponent<{
  id: string;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ id, label, value, options, onChange }) => (
  <FormGroup label={label} fieldId={id}>
    <FormSelect id={id} value={value} onChange={(_event, next) => onChange(next)}>
      {options.map((option) => (
        <FormSelectOption key={option.value} value={option.value} label={option.label} />
      ))}
    </FormSelect>
  </FormGroup>
);

export const ViewControls: React.FunctionComponent<ViewControlsProps> = ({
  payload,
  state,
  onChange,
  onReset,
  onRefresh,
  onExport,
  onExportExcel,
  isRefreshing,
  asOfControl,
}) => {
  const units = React.useMemo(() => Array.from(new Set(
    payload.rows.map((row) => unitValue(row, state.acct, payload)),
  )).sort((left, right) => left.localeCompare(right)), [payload, state.acct]);
  const filters = activeFilters(state, payload);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);

  // Everything below the toolbar row lives in the pop-up. The controls write straight through to
  // the shared view state, so each change applies to the page behind the pop-up as it is made and
  // closing it is not a separate "apply" step.
  const filterPanel = (
    <>
      {asOfControl}
      <Form className="risk-register__control-grid" aria-label="Risk Register filters">
        <SelectControl
          id="risk-register-group"
          label="Group by"
          value={state.group}
          options={GROUP_OPTIONS}
          onChange={(group) => onChange({ group: group as GroupDimension })}
        />
        <SelectControl
          id="risk-register-sort"
          label="Sort"
          value={state.sort}
          options={SORT_OPTIONS}
          onChange={(sort) => onChange({ sort: sort as ViewState['sort'] })}
        />
        <SelectControl
          id="risk-register-scope"
          label="Scope"
          value={state.scope}
          options={[{ value: 'open', label: 'Open items' }, { value: 'closed', label: 'Closed items' }, { value: 'all', label: 'All items' }]}
          onChange={(scope) => onChange({ scope: scope as ViewState['scope'] })}
        />
        <SelectControl
          id="risk-register-stage"
          label="Stage"
          value={state.stage}
          options={[{ value: 'any', label: 'Any stage' }, { value: 'todo', label: 'Not started' }, { value: 'prog', label: 'In progress' }, { value: 'limbo', label: 'Limbo' }]}
          onChange={(stage) => onChange({ stage: stage as ViewState['stage'] })}
        />
        <SelectControl
          id="risk-register-due"
          label="Due"
          value={state.due}
          options={[{ value: 'any', label: 'Any due state' }, { value: 'overdue', label: 'Overdue' }, { value: 'atrisk', label: 'At risk' }, { value: 'soon', label: 'Overdue or at risk' }, { value: 'nodate', label: 'No due date' }]}
          onChange={(due) => onChange({ due: due as ViewState['due'] })}
        />
        <SelectControl
          id="risk-register-idle"
          label="Idle"
          value={state.idle}
          options={[{ value: 'any', label: 'Any idle age' }, { value: 'stale', label: `${payload.stale_days}+ days` }, { value: 'stalled', label: `${payload.stalled_days}+ days` }]}
          onChange={(idle) => onChange({ idle: idle as ViewState['idle'] })}
        />
        <SelectControl
          id="risk-register-score"
          label="Risk score"
          value={state.score}
          options={[{ value: 'any', label: 'Any score' }, { value: 'high', label: 'High score' }, { value: 'measured', label: 'Measured' }, { value: 'unscored', label: 'Unscored' }]}
          onChange={(score) => onChange({ score: score as ViewState['score'] })}
        />
        <SelectControl
          id="risk-register-owner"
          label="Owner"
          value={state.owner}
          options={[{ value: 'any', label: 'Any owner' }, { value: 'unassigned', label: 'Unassigned' }]}
          onChange={(owner) => onChange({ owner: owner as ViewState['owner'] })}
        />
        <SelectControl
          id="risk-register-category"
          label="Risk category"
          value={state.cat}
          options={[{ value: 'any', label: 'Any category' }, ...payload.category_order.map((category) => ({ value: category, label: payload.categories[category] })), { value: '__nocat__', label: payload.no_cat_label }]}
          onChange={(cat) => onChange({ cat: cat as ViewState['cat'] })}
        />
        <SelectControl
          id="risk-register-category-state"
          label="Category state"
          value={state.catsrc}
          options={[{ value: 'any', label: 'Any category state' }, { value: 'field', label: 'Provided in Jira' }, { value: 'missing', label: 'Missing in Jira' }]}
          onChange={(catsrc) => onChange({ catsrc: catsrc as ViewState['catsrc'] })}
        />
        <SelectControl
          id="risk-register-priority"
          label="Priority"
          value={state.pri}
          options={[{ value: 'any', label: 'Any priority' }, { value: 'p1', label: 'P1 / blocker' }, { value: 'rest', label: 'Not P1' }]}
          onChange={(pri) => onChange({ pri: pri as ViewState['pri'] })}
        />
        <SelectControl
          id="risk-register-lane"
          label="Review queue lane"
          value={state.lane}
          options={[{ value: '', label: 'Any lane' }, ...payload.lane_order.map((lane) => ({ value: lane, label: lane }))]}
          onChange={(lane) => onChange({ lane: lane as ViewState['lane'] })}
        />
        <SelectControl
          id="risk-register-due-history"
          label="Due-date history"
          value={state.duemove}
          options={[{ value: 'any', label: 'Any due history' }, { value: 'moved', label: 'Moved' }, { value: 'slipped', label: 'Slipped' }, { value: 'repeat', label: 'Slipped more than once' }, { value: 'never', label: 'Never moved' }]}
          onChange={(duemove) => onChange({ duemove: duemove as ViewState['duemove'] })}
        />
      </Form>

      <div className="risk-register__accountability">
        <SelectControl
          id="risk-register-accountability-dimension"
          label="Accountability drill-down"
          value={state.acct}
          options={ACCOUNTABILITY_OPTIONS}
          onChange={(acct) => onChange({ acct: acct as GroupDimension })}
        />
        <SelectControl
          id="risk-register-accountability-unit"
          label="Unit"
          value={state.unit}
          options={[{ value: '', label: 'All units' }, ...units.map((unit) => ({ value: `${state.acct}:${unit}`, label: unit }))]}
          onChange={(unit) => onChange({ unit })}
        />
      </div>
    </>
  );

  return (
    <div className="risk-register__controls">
      <Tabs
        className="risk-register__area-tabs"
        activeKey={state.area}
        onSelect={(_event, key) => onChange({ area: String(key) })}
        aria-label="Platform area"
      >
        {[
          <Tab key="ALL" eventKey="ALL" title={<TabTitleText>All areas</TabTitleText>} />,
          ...payload.svp_order.map((area) => (
            <Tab key={area} eventKey={area} title={<TabTitleText>{area}</TabTitleText>} />
          )),
        ]}
      </Tabs>

      <div className="risk-register__toolbar">
        <Button
          variant="secondary"
          icon={<FilterIcon />}
          onClick={() => setIsFilterPanelOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={isFilterPanelOpen}
        >
          Filters{filters.length > 0 && <Badge isRead className="risk-register__filter-count">{filters.length}</Badge>}
        </Button>
        <TextInput
          id="risk-register-search"
          className="risk-register__toolbar-search"
          value={state.q}
          onChange={(_event, q) => onChange({ q })}
          type="search"
          aria-label="Search the register"
          placeholder="Ticket, summary, project, owner, initiative, category, or status"
        />
        <div className="risk-register__toolbar-actions">
          <Button variant="link" onClick={onReset} isDisabled={filters.length === 0}>Reset view</Button>
          <Button variant="secondary" onClick={onExport}>Export CSV</Button>
          <Button variant="secondary" onClick={onExportExcel}>Export Excel</Button>
          <Button variant="primary" onClick={() => void onRefresh()} isLoading={isRefreshing}>
            Refresh data
          </Button>
        </div>
      </div>

      {filters.length > 0 && (
        <ChipGroup categoryName="Active filters" className="risk-register__active-filters">
          {filters.map((filter) => (
            <Chip key={filter.label} onClick={() => onChange(filter.change)}>
              {filter.label}
            </Chip>
          ))}
        </ChipGroup>
      )}

      <Modal
        variant={ModalVariant.medium}
        title={PICK_SECTION.title}
        description={PICK_SECTION.hint}
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        className="risk-register__filter-modal"
        actions={[
          <Button key="done" variant="primary" onClick={() => setIsFilterPanelOpen(false)}>Done</Button>,
          <Button key="reset" variant="link" onClick={onReset} isDisabled={filters.length === 0}>Reset view</Button>,
        ]}
      >
        {filterPanel}
      </Modal>
    </div>
  );
};
